import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cog, PlusCircle, FilterIcon, ArrowLeft, Building2, FlaskConical } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import DynamicFormModal from '@/components/DynamicFormModal';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import AppliedFilters from '@/components/AppliedFilters';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { washingProgramsColumns, columnsExcel } from './utils/washingProgramsColumns';
import { getWashingPrograms, createWashingProgram, updateWashingProgram, deleteWashingProgram, exportWashingPrograms } from './Services/washingPrograms.services';
import ProgramProductsModal from './components/ProgramProductsModal';

const WashingProgramsPage = () => {
  const [searchParams] = useSearchParams();
  const idLavanderia = searchParams.get('lavanderia');
  const [laundryName, setLaundryName] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(
    idLavanderia ? { query: { 'lavanderia.id': idLavanderia }, page: 1, limit: 10 } : { query: {}, page: 1, limit: 10 }
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { toast } = useToast();
  const [laundriesOptions, setLaundriesOptions] = useState([]);

  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [programForProducts, setProgramForProducts] = useState(null);

  const buildBackendQuery = (rawQuery) => {
    const query = { ...rawQuery };
    if (query.nombre && typeof query.nombre === 'string') {
      const value = query.nombre.trim();
      if (value) {
        query.nombre = { $regex: value, $options: 'i' };
      } else {
        delete query.nombre;
      }
    }
    return query;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWashingPrograms({
        ...filters,
        query: buildBackendQuery(filters.query),
      });
      setData(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudieron cargar los programas de lavado.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch laundry name if coming from CentroParametrizacionPage
  useEffect(() => {
    if (idLavanderia) {
      const fetchLaundryName = async () => {
        try {
          const { fetchLaundryById } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
          const laundry = await fetchLaundryById(idLavanderia);
          setLaundryName(laundry?.nombre || '');
        } catch (e) {
          console.error('Error fetching laundry:', e);
        }
      };
      fetchLaundryName();
    }
  }, [idLavanderia]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { fetchLaundries } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
        const response = await fetchLaundries({ limit: 100, offset: 1, query: { estado: 'Activo' } });
        if (!mounted) return;
        setLaundriesOptions(response.data.map(laundry => ({ value: laundry._id, label: laundry.nombre })));
      } catch (error) {
        console.error('Error cargando centros de lavado:', error);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleFilter = (newFilters) => {
    setFilters((prev) => ({ ...prev, query: newFilters, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({ ...prev, query: {}, page: 1 }));
  };

  const handleRemoveFilter = (filterKey) => {
    setFilters((prev) => {
      const newQuery = { ...prev.query };
      delete newQuery[filterKey];
      return { ...prev, query: newQuery, page: 1 };
    });
  };

  const filterFields = useMemo(() => [
    {
      id: 'nombre',
      label: 'Nombre del Programa',
      type: 'text',
      placeholder: 'Buscar por nombre...',
    },
    {
      id: 'lavanderia.id',
      label: 'Centro de Lavado',
      type: 'select',
      options: laundriesOptions,
    },
  ], [laundriesOptions]);

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await updateWashingProgram(editingItem._id, formData);
        toast({ title: 'Éxito', description: 'Programa de lavado actualizado.' });
      } else {
        await createWashingProgram(formData);
        toast({ title: 'Éxito', description: 'Programa de lavado creado.' });
      }
      fetchData();
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast({ title: 'Error', description: `No se pudo guardar el programa. ${error.message}`, variant: 'destructive' });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteWashingProgram(itemToDelete._id);
        toast({ title: 'Éxito', description: 'Programa de lavado eliminado.' });
        fetchData();
      } catch (error) {
        toast({ title: 'Error', description: 'No se pudo eliminar el programa.', variant: 'destructive' });
      }
    }
    setIsDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleAction = (action, item) => {
    if (action === 'edit') handleEdit(item);
    else if (action === 'delete') handleDelete(item);
    else if (action === 'products') {
      setProgramForProducts(item);
      setIsProductsModalOpen(true);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="p-4 md:p-6 lg:p-8">
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {idLavanderia && (
              <Button asChild variant="outline" size="icon" className="shrink-0">
                <Link to={`/parametrizacion/centros/${idLavanderia}`}>
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              </Button>
            )}
            <div className="bg-primary/10 p-3 rounded-lg"><Cog className="h-8 w-8 text-primary" /></div>
            <div>
              <CardTitle className="text-2xl">
                Programas de Lavado
                {laundryName && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    — {laundryName}
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                {idLavanderia
                  ? 'Gestiona los programas de lavado del centro de lavado.'
                  : 'Gestiona los programas de lavado del sistema.'}
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <ExportExcelButton
              service={async ({ query }) => {
                const res = await getWashingPrograms({ page: 1, limit: 10000, query: buildBackendQuery(query || {}) });
                return res.data || [];
              }}
              filters={filters.query}
              columns={useMemo(() => columnsExcel, [])}
              fileName="Programas_de_Lavado"
            />
            <Button onClick={() => { setEditingItem(null); setIsModalOpen(true); }}><PlusCircle className="h-4 w-4 mr-2" />Crear Nuevo</Button>
            <Button onClick={() => setIsFilterOpen(true)} variant="outline"><FilterIcon className="h-4 w-4 mr-2" />Filtrar</Button>
          </div>
        </CardHeader>
        <CardContent>
          <AppliedFilters
            filters={filters.query}
            fields={filterFields}
            onClear={handleClearFilters}
            onRemoveFilter={handleRemoveFilter}
            onApply={handleFilter}
          />
          <DataTable
            data={data}
            columns={washingProgramsColumns}
            isLoading={loading}
            onAction={handleAction}
            page={filters.page}
            limit={filters.limit}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isModalOpen && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleSave}
            fields={washingProgramsColumns}
            item={editingItem}
            title={editingItem ? 'Editar Programa de Lavado' : 'Crear Programa de Lavado'}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isFilterOpen && (
          <FilterDrawer
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onFilter={handleFilter}
            onApply={handleFilter}
            fields={filterFields}
            initialFilters={filters.query}
          />
        )}
      </AnimatePresence>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. Se eliminará permanentemente el programa.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ProgramProductsModal
        isOpen={isProductsModalOpen}
        onClose={() => {
          setIsProductsModalOpen(false);
          setProgramForProducts(null);
        }}
        program={programForProducts}
        onSuccess={fetchData}
      />
    </motion.div>
  );
};

export default WashingProgramsPage;
